<template>
  <div v-if="campaignId">
    <event-header-hero
      class="event-controls-hero"
      :title="$t('eventpage.settings_screen_controls', 'Screen Controls')"
    >
      <btn class="tw-mx-auto" type="secondary-v2" @click="openPreview">
        {{ $t("eventpage.settings_section_btn_view_screen", "View Screen") }}
      </btn>
    </event-header-hero>
    <div class="tw-pb-[82px]">
      <div class="event-search">
        <div class="form-wrapper">
          <div class="form-section">
            <div class="form-section__header">
              {{ $t("eventpage.settings_section_visibility", "Visibility") }}
            </div>
            <div class="form-section__body">
              <event-page-open-settings-item
                :value="$t('eventpage.settings_section_enable', 'Enable/Disable Event View')"
                :text="$t('eventpage.settings_section_enable', 'Enable/Disable Event View')"
              >
                <checkbox-switch :value="form.value" @input="onFormValueInput" :disabled="loading">
                  {{ $t("eventpage.settings_section_enable", "Enable/Disable Event View") }}
                </checkbox-switch>
              </event-page-open-settings-item>
            </div>
          </div>
        </div>

        <input-text-number
          v-if="form.value"
          type="text"
          :placeholder="$t('eventpage.settings_search_setting', 'Search setting')"
          v-model="search"
        >
          <template #icon>
            <icon-search />
          </template>
        </input-text-number>
      </div>

      <!-- Main view -->
      <div class="form-wrapper" v-show="!isShowSettings && form.value">
        <div
          class="form-section"
          v-for="([key, section], index) in Object.entries(schema).filter(([k]) => k !== propName)"
          :key="index"
        >
          <div class="form-section__header">{{ schema[key][propName].title }}</div>
          <div class="form-section__body">
            <event-page-settings-list-item
              v-for="([setting_key, setting], ind) in Object.entries(section).filter(
                ([k]) => k !== propName
              )"
              :key="ind"
              :data="setting[propName]"
              @toggle="showSetting(setting_key)"
            />
          </div>
        </div>
      </div>

      <!-- Open view -->
      <div class="form-wrapper" v-show="isShowSettings || isSearched">
        <!-- language -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.general.language[propName]"
          v-show="showSettingsKey === 'language' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.general.language[propName].title"
          >
            <div class="event-select">
              <div class="event-select__label">
                {{ schema.general.language[propName].title }}
              </div>
              <app-select
                class="event-select__select"
                v-model="form.general.language"
                :searchable="false"
                :show-labels="false"
                track-by="value"
                label="text"
                :allow-empty="false"
                :options="schema.general.language.options"
                singleModel
              >
                <template #caret>
                  <span class="multiselect__select">
                    <icon-arrow-right class="multiselect__select__caret" />
                  </span>
                </template>
              </app-select>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <!-- confetti -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.confetti[propName]"
          v-show="showSettingsKey === 'confetti' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.confetti.show_confetti_on_donation_above.title"
          >
            <div
              class="event-rule-switch"
              :class="{ 'event-rule-switch--off': !form.display.confetti.donation_above_enabled }"
            >
              <input-text-number
                :label="schema.display.confetti.show_confetti_on_donation_above.title"
                type="number"
                size="200"
                :prefix="currency_sign"
                v-model="form.display.confetti.show_confetti_on_donation_above"
              />
              <checkbox-switch v-model="form.display.confetti.donation_above_enabled" />
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.confetti.show_confetti_on_ambassador_goal_above.title"
          >
            <div
              class="event-rule-switch"
              :class="{
                'event-rule-switch--off': !form.display.confetti.ambassador_goal_above_enabled,
              }"
            >
              <input-text-number
                :label="schema.display.confetti.show_confetti_on_ambassador_goal_above.title"
                type="number"
                size="200"
                :prefix="currency_sign"
                v-model="form.display.confetti.show_confetti_on_ambassador_goal_above"
              />
              <checkbox-switch v-model="form.display.confetti.ambassador_goal_above_enabled" />
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.confetti.show_confetti_on_total_raised_above_percent.title"
          >
            <div
              class="event-rule-switch"
              :class="{
                'event-rule-switch--off': !form.display.confetti.total_raised_above_percent_enabled,
              }"
            >
              <input-text-number
                :label="schema.display.confetti.show_confetti_on_total_raised_above_percent.title"
                type="number"
                size="134"
                sufix="%"
                v-model="form.display.confetti.show_confetti_on_total_raised_above_percent"
              />
              <checkbox-switch v-model="form.display.confetti.total_raised_above_percent_enabled" />
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.confetti.confetti_animation_type.title"
          >
            <div class="event-select">
              <div class="event-select__label">
                {{ schema.display.confetti.confetti_animation_type.title }}
              </div>
              <app-select
                class="event-select__select"
                v-model="form.display.confetti.confetti_animation_type"
                :searchable="false"
                :show-labels="false"
                track-by="value"
                label="text"
                :allow-empty="false"
                :options="schema.display.confetti.confetti_animation_type.options"
                singleModel
              >
                <template #caret>
                  <span class="multiselect__select">
                    <icon-arrow-right class="multiselect__select__caret" />
                  </span>
                </template>
              </app-select>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <!-- pin -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.pin[propName]"
          v-show="showSettingsKey === 'pin' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.pin.donation_total_or_ambassador_goal_above.title"
          >
            <div
              class="event-rule-switch"
              :class="{
                'event-rule-switch--off':
                  !form.display.pin.donation_total_or_ambassador_goal_above_enabled,
              }"
            >
              <input-text-number
                :label="schema.display.pin.donation_total_or_ambassador_goal_above.title"
                type="number"
                size="200"
                :prefix="currency_sign"
                :placeholder="$t('eventpage.settings_display_pin_amount_placeholder', 'Amount')"
                v-model="form.display.pin.donation_total_or_ambassador_goal_above"
              />
              <checkbox-switch
                v-model="form.display.pin.donation_total_or_ambassador_goal_above_enabled"
              />
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.pin[propName].secondsection"
          v-show="showSettingsKey === 'pin' || isSearched"
        >
          <event-page-open-settings-item
            v-if="filteredDonations.length"
            v-model="search"
            :text="schema.display.pin.donation_total_or_ambassador_goal_above.title"
          >
            <div class="event-checkbox-helper">
              <checkbox-switch
                class="event-checkbox-helper__item"
                v-for="(item, ind) in filteredDonations"
                :key="ind"
                v-model="pinnedItems[`${item.team_id}-${item.donation_id}`]"
              >
                <span class="event-checkbox-helper__label">
                  <span class="event-checkbox-helper__text">
                    {{ item.donor_name || item.team_name }}
                  </span>
                  <span class="event-checkbox-helper__value">
                    {{ currency_sign }}{{ item.donation_amount }}
                  </span>
                </span>
              </checkbox-switch>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <!-- commitment_cards -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.commitment_cards[propName]"
          v-show="showSettingsKey === 'commitment_cards' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.show_donors.title"
          >
            <checkbox-switch v-model="form.display.commitment_cards.show_donors">
              {{ schema.display.commitment_cards.show_donors.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.show_ambassadors.title"
          >
            <checkbox-switch v-model="form.display.commitment_cards.show_ambassadors">
              {{ schema.display.commitment_cards.show_ambassadors.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.show_joined_donors_and_ambassadors.title"
          >
            <checkbox-switch
              v-model="form.display.commitment_cards.show_joined_donors_and_ambassadors"
            >
              {{ schema.display.commitment_cards.show_joined_donors_and_ambassadors.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.commitment_cards[propName].secondsection"
          v-show="showSettingsKey === 'commitment_cards' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.mode.title"
          >
            <div class="event-radio-group-wrapper">
              {{ schema.display.commitment_cards.mode.title }}
              <b-form-group class="event-radio-group">
                <b-form-radio-group
                  v-model="form.display.commitment_cards.mode"
                  :options="schema.display.commitment_cards.mode.options"
                  name="commitment_cards-mode"
                  button-variant="primary"
                  buttons
                ></b-form-radio-group>
              </b-form-group>
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.new_don_cards_should_apear.title"
          >
            <div class="event-commitment-cards-controlled-setting">
              <checkbox-switch v-model="new_don_cards_should_apear_value">
                {{ schema.display.commitment_cards.new_don_cards_should_apear.title }}
              </checkbox-switch>
              <small v-if="form.display.commitment_cards.mode !== 'mixed'" class="text-muted">
                Controlled by mode setting above
              </small>
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.new_team_cards_should_apear.title"
          >
            <div class="event-commitment-cards-controlled-setting">
              <checkbox-switch v-model="new_team_cards_should_apear_value">
                {{ schema.display.commitment_cards.new_team_cards_should_apear.title }}
              </checkbox-switch>
              <small v-if="form.display.commitment_cards.mode !== 'mixed'" class="text-muted">
                Controlled by mode setting above
              </small>
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.layout_cards_number.title"
          >
            <div class="event-radio-group-wrapper">
              {{ schema.display.commitment_cards.layout_cards_number.title }}
              <b-form-group class="event-radio-group">
                <b-form-radio-group
                  v-model="form.display.commitment_cards.layout_cards_number"
                  :options="schema.display.commitment_cards.layout_cards_number.options"
                  name="commitment_cards-layout_cards_number"
                  button-variant="primary"
                  buttons
                ></b-form-radio-group>
              </b-form-group>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.commitment_cards[propName].thirdsection"
          v-show="showSettingsKey === 'commitment_cards' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.commitment_cards.same_time_commitment_timeout.title"
          >
            <input-text-number
              :label="schema.display.commitment_cards.same_time_commitment_timeout.title"
              type="number"
              size="110"
              v-model="form.display.commitment_cards.same_time_commitment_timeout"
            />
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <!-- scroll_lines -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.scroll_lines[propName]"
          v-show="showSettingsKey === 'scroll_lines' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="
              schema.display.scroll_lines[propName].title +
              ' ' +
              schema.display.scroll_lines.row1.active.title +
              ' ' +
              schema.display.scroll_lines.row1.title.title +
              ' ' +
              schema.display.scroll_lines.row1.show_amount_range_from.title +
              ' ' +
              schema.display.scroll_lines.row1.show_amount_range_to.title
            "
          >
            <div class="event-scrolling-settings-wrapper">
              <checkbox-switch v-model="form.display.scroll_lines.row1.active">
                <span class="tw-font-bold">
                  {{ schema.display.scroll_lines.row1.active.title }}
                </span>
              </checkbox-switch>
              <div class="event-select">
                <div class="event-select__label">
                  {{ schema.display.scroll_lines.row1.scroll.title }}
                </div>
                <app-select
                  class="event-select__select"
                  v-model="form.display.scroll_lines.row1.scroll"
                  :searchable="false"
                  :show-labels="false"
                  track-by="value"
                  label="text"
                  :allow-empty="false"
                  :options="schema.display.scroll_lines.row1.scroll.options"
                  singleModel
                >
                  <template #caret>
                    <span class="multiselect__select">
                      <icon-arrow-right class="multiselect__select__caret" />
                    </span>
                  </template>
                </app-select>
              </div>

              <input-text-number
                class="event-scrolling-settings-wrapper__input"
                :label="schema.display.scroll_lines.row1.title.title"
                type="text"
                size="310"
                v-model="form.display.scroll_lines.row1.title"
              />
              <div class="event-two-inputs-wrapper">
                <span class="event-two-inputs-wrapper__title">
                  {{ schema.display.scroll_lines.row1.show_amount_range_from.title }}
                </span>
                <input-text-number
                  class="event-two-inputs-wrapper__input"
                  type="number"
                  size="200"
                  :prefix="currency_sign"
                  v-model="form.display.scroll_lines.row1.show_amount_range_from"
                />
                <span>
                  {{ schema.display.scroll_lines.row1.show_amount_range_to.title }}
                </span>
                <input-text-number
                  class="event-two-inputs-wrapper__input"
                  type="number"
                  size="200"
                  :prefix="currency_sign"
                  v-model="form.display.scroll_lines.row1.show_amount_range_to"
                />
              </div>
            </div>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="
              schema.display.scroll_lines[propName].title +
              ' ' +
              schema.display.scroll_lines.row2.active.title +
              ' ' +
              schema.display.scroll_lines.row2.title.title +
              ' ' +
              schema.display.scroll_lines.row2.show_amount_range_from.title +
              ' ' +
              schema.display.scroll_lines.row2.show_amount_range_to.title
            "
          >
            <div class="event-scrolling-settings-wrapper">
              <checkbox-switch v-model="form.display.scroll_lines.row2.active">
                <span class="tw-font-bold">
                  {{ schema.display.scroll_lines.row2.active.title }}
                </span>
              </checkbox-switch>
              <div class="event-select">
                <div class="event-select__label">
                  {{ schema.display.scroll_lines.row2.scroll.title }}
                </div>
                <app-select
                  class="event-select__select"
                  v-model="form.display.scroll_lines.row2.scroll"
                  :searchable="false"
                  :show-labels="false"
                  track-by="value"
                  label="text"
                  :allow-empty="false"
                  :options="schema.display.scroll_lines.row2.scroll.options"
                  singleModel
                >
                  <template #caret>
                    <span class="multiselect__select">
                      <icon-arrow-right class="multiselect__select__caret" />
                    </span>
                  </template>
                </app-select>
              </div>

              <input-text-number
                class="event-scrolling-settings-wrapper__input"
                :label="schema.display.scroll_lines.row2.title.title"
                type="text"
                size="310"
                v-model="form.display.scroll_lines.row2.title"
              />
              <div class="event-two-inputs-wrapper">
                <span class="event-two-inputs-wrapper__title">
                  {{ schema.display.scroll_lines.row2.show_amount_range_from.title }}
                </span>
                <input-text-number
                  class="event-two-inputs-wrapper__input"
                  type="number"
                  size="200"
                  :prefix="currency_sign"
                  v-model="form.display.scroll_lines.row2.show_amount_range_from"
                />
                <span>
                  {{ schema.display.scroll_lines.row2.show_amount_range_to.title }}
                </span>
                <input-text-number
                  class="event-two-inputs-wrapper__input"
                  type="number"
                  size="200"
                  :prefix="currency_sign"
                  v-model="form.display.scroll_lines.row2.show_amount_range_to"
                />
              </div>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <!-- section_stats -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.display.section_stats[propName]"
          v-show="showSettingsKey === 'section_stats' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_total_raised.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_total_raised">
              {{ schema.display.section_stats.show_total_raised.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_total_goal.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_total_goal">
              {{ schema.display.section_stats.show_total_goal.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_number_donors.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_number_donors">
              {{ schema.display.section_stats.show_number_donors.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_number_ambassadors.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_number_ambassadors">
              {{ schema.display.section_stats.show_number_ambassadors.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_total_ambassadors_goal.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_total_ambassadors_goal">
              {{ schema.display.section_stats.show_total_ambassadors_goal.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_total_donation_pledges.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_total_donation_pledges">
              {{ schema.display.section_stats.show_total_donation_pledges.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
          <event-page-open-settings-item
            v-model="search"
            :text="schema.display.section_stats.show_total_raised_in_percentage.title"
          >
            <checkbox-switch v-model="form.display.section_stats.show_total_raised_in_percentage">
              {{ schema.display.section_stats.show_total_raised_in_percentage.title }}
            </checkbox-switch>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <!-- color -->
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.general.color[propName]"
          v-show="showSettingsKey === 'color' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="
              schema.general.color.theme.classic.title +
              ' ' +
              schema.general.color.theme.elegant.title
            "
          >
            <div class="event-color-theme-settings-wrapper">
              <div class="event-color-theme-settings-wrapper__item">
                {{ schema.general.color.theme.classic.title }}
                <img
                  src="@/assets/img/event-page/classic.png"
                  :class="[
                    form.general.theme === schema.general.color.theme.classic.value ? 'active' : '',
                  ]"
                  alt="classic-theme"
                />
                <checkbox-switch
                  v-model="form.general.theme"
                  :unchecked-value="schema.general.color.theme.elegant.value"
                  :checked-value="schema.general.color.theme.classic.value"
                />
              </div>

              <div class="event-color-theme-settings-wrapper__item">
                {{ schema.general.color.theme.elegant.title }}
                <img
                  src="@/assets/img/event-page/elegant.png"
                  :class="[
                    form.general.theme === schema.general.color.theme.elegant.value ? 'active' : '',
                  ]"
                  alt="elegant-theme"
                />
                <checkbox-switch
                  v-model="form.general.theme"
                  :unchecked-value="schema.general.color.theme.classic.value"
                  :checked-value="schema.general.color.theme.elegant.value"
                />
              </div>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <event-page-open-settings-wrapper
          @toggle="showSetting"
          :data="schema.general.color[propName].secondsection"
          v-show="showSettingsKey === 'color' || isSearched"
        >
          <event-page-open-settings-item
            v-model="search"
            :text="
              schema.general.color.color.title +
              ' ' +
              schema.general.color.imgs
                .map((img) => `${img.title} ${img.description} ${img.btnText}`)
                .join(' ') +
              ' ' +
              schema.general.color.showLogo.title
            "
          >
            <div class="event-color-settings-wrapper">
              <div
                v-for="img in schema.general.color.imgs"
                :key="img.id"
                class="event-color-settings-wrapper-files"
              >
                <img v-if="form.general[img.id]" :src="form.general[img.id]" alt="" />
                <div class="event-color-settings-wrapper-files-text">
                  <p>{{ img.title }}</p>
                  <span>{{ img.description }}</span>
                </div>
                <input-file
                  :file="img"
                  :loading="loadingImageId === img.id"
                  @change="addImg($event, img.id)"
                />
              </div>
              <checkbox-switch
                class="event-color-settings-wrapper__logo-switch"
                v-model="form.general.organization_logo_enabled"
              >
                {{ schema.general.color.showLogo.title }}
              </checkbox-switch>

              <p class="event-color-settings-wrapper__title">
                {{ schema.general.color.color.title }}
              </p>
              <div class="event-color-settings-wrapper-controls">
                <div
                  class="event-color-settings-wrapper-controls__item"
                  v-for="item in 3"
                  :key="item"
                >
                  <p>Color #{{ item }}</p>
                  <div>
                    <div class="event-input-color-wrapper">
                      <div class="event-input-color-wrapper-inner">
                        <input
                          type="color"
                          class="event-input-color"
                          v-model="form.general[`color${item}`]"
                        />
                      </div>
                    </div>
                    <input
                      type="text"
                      class="event-input-color-text"
                      v-model="form.general[`color${item}`]"
                    />
                  </div>
                  <small
                    v-if="item === 3 && campaignColors"
                    class="event-color-settings-wrapper-controls__hint"
                  >
                    {{ schema.general.color.color.notFromCampaign }}
                  </small>
                </div>
              </div>
              <div class="event-color-settings-wrapper-result">
                <sample-donor-card
                  :color1="form.general.color1"
                  :color2="form.general.color2"
                  :color3="form.general.color3"
                />
              </div>
              <div v-if="campaignColors" class="event-color-settings-wrapper__actions">
                <btn type="secondary-v2" @click="toggleColors">
                  {{
                    usesScreenColors
                      ? schema.general.color.color.useCampaign
                      : schema.general.color.color.reset
                  }}
                </btn>
              </div>
            </div>
          </event-page-open-settings-item>
        </event-page-open-settings-wrapper>
        <btn type="primary" :loading="loading" @click="save">
          {{ $t("eventpage.settings_section_btn_text_save", "Save Changes") }}
        </btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import EventPageSettingsListItem from "./event-page-settings-list-item.vue";
import EventPageOpenSettingsWrapper from "./event-page-open-settings-wrapper.vue";
import EventPageOpenSettingsItem from "./event-page-open-settings-item.vue";
import InputTextNumber from "@/views/event-page/ui/input.vue";
import iconSearch from "@/views/event-page/icons/search.vue";
import appSelect from "@/common-components/ui-elements/app-select.vue";
import iconArrowRight from "@/views/event-page/icons/arrow-right.vue";
import SampleDonorCard from "./event-page-sample-donor-card.vue";
import axios from "axios";
import urls from "@/constants/urls";
import InputFile from "@/views/event-page/ui/input-file.vue";
import Btn from "@/views/event-page/ui/btn.vue";
import EventHeaderHero from "@/views/event-page/header/header-hero.vue";
import CheckboxSwitch from "@/views/event-page/ui/checkbox-switch.vue";
import AddTeamHiddenByDefault from "@/views/campaigns/campaign-settings/settings-schemas/add_team_hidden_by_default.schema.js";
import PlaceNewDonationsOnHold from "@/views/campaigns/campaign-settings/settings-schemas/place_new_donations_on_hold.schema.js";

// Screen colors used when the campaign has no colors of its own
const SCREEN_COLORS = { color1: "#120A25", color2: "#FFD8A6", color3: "#B1719E" };

export default {
  name: "event-page-settings",
  data() {
    return {
      pinnedItems: {},
      new_don_cards_should_apear_value: false,
      new_team_cards_should_apear_value: false,
      commonMeta: {
        new_don_cards_should_apear: {
          data: null,
          metaName: "place_new_donations_on_hold",
          scheme: PlaceNewDonationsOnHold((key, fallback) => this.$t(key, fallback)),
          key: "value",
          firstEnableValue: false,
        },
        new_team_cards_should_apear: {
          data: null,
          metaName: "add_team_hidden_by_default",
          scheme: AddTeamHiddenByDefault((key, fallback) => this.$t(key, fallback)),
          key: "value",
          firstEnableValue: true,
        },
      },
      loading: false,
      form: {},
      showSettingsKey: "",
      propName: "properties",
      search: "",
      loadingImageId: null,
      campaignColors: null,
    };
  },

  components: {
    appSelect,
    EventPageOpenSettingsItem,
    EventPageSettingsListItem,
    EventPageOpenSettingsWrapper,
    InputTextNumber,
    iconSearch,
    iconArrowRight,
    SampleDonorCard,
    InputFile,
    Btn,
    EventHeaderHero,
    CheckboxSwitch,
  },

  computed: {
    ...mapState({
      oID: () => localStorage.getItem("orgId"),
      campaign: (state) => state.campaign?.campaign?.data?.attributes || {},
      eventDonationsList: (state) => {
        return state.eventPlatform.eventDonations;
      },
      eventSettingsList: (state) => {
        return state.eventPlatform.eventSettingsSchema;
      },
      eventSettingsSchemaId: (state) => {
        return state.eventPlatform.eventSettingsSchemaId;
      },
    }),

    ...mapGetters({
      getSelectedCampaign: "getSelectedCampaign",
    }),

    currency_sign() {
      return this.campaign?.currency_sign || "$";
    },

    filteredDonations() {
      if (!this.form?.display?.pin?.manual_list || !this.eventDonationsList) {
        return [];
      }

      return this.eventDonationsList.filter((donation) =>
        this.form.display.pin.manual_list.some(
          (pinned) =>
            pinned.team_id === donation.team_id && pinned.donation_id === donation.donation_id
        )
      );
    },

    campaignId() {
      return this.$store.state.dashboard.selectedCampaign;
    },

    isShowSettings() {
      if (this.isSearched) {
        return true;
      } else {
        return this.showSettingsKey;
      }
    },

    isSearched() {
      return this.search.length > 0;
    },

    orgLogo() {
      return this.$store.state.account.account?.data?.attributes?.logo || "";
    },

    // The campaign's own colors replace the first two, the third has no campaign counterpart
    defaultColors() {
      if (!this.campaignColors) {
        return { ...SCREEN_COLORS };
      }

      return {
        ...SCREEN_COLORS,
        color1: this.campaignColors.primary,
        color2: this.campaignColors.secondary,
      };
    },

    usesScreenColors() {
      return Object.entries(SCREEN_COLORS).every(
        ([key, color]) => this.form.general?.[key]?.toLowerCase() === color.toLowerCase()
      );
    },

    orgLanguage() {
      const lang = this.$store.state.account.account?.data?.attributes?.lang;
      const supported = this.schema.general.language.options.some(
        (option) => option.value === lang
      );
      return supported ? lang : "en";
    },

    schema() {
      return {
        general: {
          properties: {
            title: this.$t("eventpage.settings_section_general_title", "General"),
          },
          language: {
            properties: {
              title: this.$t("eventpage.settings_general_language_title", "Display Language"),
              description: this.$t(
                "eventpage.settings_general_language_description",
                "Select preferred language"
              ),
              icon: "earth",
            },
            options: [
              {
                text: this.$t("eventpage.settings_general_language_hebrew_text", "Hebrew"),
                value: "he",
              },
              {
                text: this.$t("eventpage.settings_general_language_english_text", "English"),
                value: "en",
              },
            ],
          },
          color: {
            properties: {
              title: this.$t("eventpage.settings_general_color_title", "Theme"),
              description: this.$t(
                "eventpage.settings_general_color_description",
                "Classic or Gala"
              ),
              icon: "laptop",
              secondsection: {
                title: this.$t("eventpage.settings_general_color_secondsection_title", "Colors"),
                icon: "laptop",
              },
            },
            theme: {
              classic: {
                title: this.$t("eventpage.settings_general_color_theme_classic_title", "Classic"),
                value: "classic",
              },
              elegant: {
                title: this.$t("eventpage.settings_general_color_theme_elegant_title", "Elegant"),
                value: "elegant",
              },
            },
            imgs: [
              {
                id: "background_image",
                title: this.$t(
                  "eventpage.settings_general_color_background_image_title",
                  "Upload Background Image for Screen"
                ),
                description: this.$t(
                  "eventpage.settings_general_color_background_image_description",
                  "For best quality, recommended size 1920x1080, max 2MB"
                ),
                btnText: this.$t(
                  "eventpage.settings_general_color_background_image_btn_text",
                  "Upload Image"
                ),
              },
              {
                id: "organization_logo",
                title: this.$t(
                  "eventpage.settings_general_color_organization_logo_title",
                  "Upload Organization Logo"
                ),
                description: this.$t(
                  "eventpage.settings_general_color_organization_logo_description",
                  "This will be displayed on the screen. If you are using a dark background, make sure the logo is a light color."
                ),
                btnText: this.$t(
                  "eventpage.settings_general_color_organization_logo_btn_text",
                  "Upload Image"
                ),
              },
            ],
            color: {
              title: this.$t(
                "eventpage.settings_general_color_color_title",
                "Choose Colors for your campaign"
              ),
              notFromCampaign: this.$t(
                "eventpage.settings_general_color_color3_not_from_campaign",
                "This color is not taken from the campaign colors. Adjust it to match them."
              ),
              reset: this.$t("eventpage.settings_general_color_reset", "Reset to default colors"),
              useCampaign: this.$t(
                "eventpage.settings_general_color_use_campaign",
                "Use campaign colors"
              ),
            },
            showLogo: {
              title: this.$t(
                "eventpage.settings_general_color_show_logo_title",
                "Show logo on screen"
              ),
            },
          },
        },
        display: {
          properties: {
            title: this.$t("eventpage.settings_section_display_title", "Display"),
          },
          commitment_cards: {
            properties: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_title",
                "Commitment Cards"
              ),
              description: this.$t(
                "eventpage.settings_display_commitment_cards_description",
                "Choose which commitments to display on screen"
              ),
              icon: "list",
              secondsection: {
                title: this.$t(
                  "eventpage.settings_display_commitment_cards_secondsection_title",
                  "Layout"
                ),
                icon: "list",
              },
              thirdsection: {
                title: this.$t(
                  "eventpage.settings_display_commitment_cards_thirdsection_title",
                  "Time"
                ),
                icon: "list",
              },
            },
            show_donors: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_show_donors_title",
                "Donor Commitments"
              ),
            },
            show_ambassadors: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_show_ambassadors_title",
                "Ambassador Commitment"
              ),
            },
            show_joined_donors_and_ambassadors: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_show_joined_donors_and_ambassadors_title",
                "Donor+Ambassador Commitments"
              ),
            },
            mode: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_mode_title",
                "New cards should appear on the screen"
              ),
              options: [
                {
                  text: this.$t(
                    "eventpage.settings_display_commitment_cards_mode_manual",
                    "Manually"
                  ),
                  value: "manual",
                },
                {
                  text: this.$t(
                    "eventpage.settings_display_commitment_cards_mode_automatic",
                    "Automatically"
                  ),
                  value: "automatic",
                },
              ],
            },
            new_don_cards_should_apear: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_new_don_cards_should_apear_title",
                "New donation cards should appear on the screen"
              ),
            },
            new_team_cards_should_apear: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_new_team_cards_should_apear_title",
                "New team cards should appear on the screen"
              ),
            },
            same_time_commitment_timeout: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_same_time_commitment_timeout_title",
                "If multiple commitments get submitted at the same time, each card should be displayed for"
              ),
            },
            layout_cards_number: {
              title: this.$t(
                "eventpage.settings_display_commitment_cards_layout_cards_number_title",
                "Choose maximum donor cards to displayon screen"
              ),
              options: [
                { text: "10", value: 10 },
                { text: "18", value: 18 },
                { text: "26", value: 26 },
              ],
            },
          },
          scroll_lines: {
            properties: {
              title: this.$t("eventpage.settings_display_scroll_lines_title", "Scrolling"),
              description: this.$t(
                "eventpage.settings_display_scroll_lines_description",
                "Option to scroll donations and ambassadors"
              ),
              icon: "move",
            },
            row1: {
              active: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row1_active_title",
                  "1st Row"
                ),
              },
              title: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row1_title_title",
                  "Choose title to be displayed"
                ),
              },
              scroll: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row1_scroll_title",
                  "Scroll"
                ),
                options: [
                  {
                    text: this.$t(
                      "eventpage.settings_display_scroll_lines_row1_scroll_options_donors",
                      "Donors"
                    ),
                    value: "donors",
                  },
                  {
                    text: this.$t(
                      "eventpage.settings_display_scroll_lines_row1_scroll_options_ambassadors",
                      "Ambassadors"
                    ),
                    value: "ambassadors",
                  },
                ],
              },
              show_amount_range_from: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row1_show_amount_range_from_title",
                  "Show amounts from"
                ),
              },
              show_amount_range_to: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row1_show_amount_range_to_title",
                  "to"
                ),
              },
            },
            row2: {
              active: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row2_active_title",
                  "2nd Row"
                ),
              },
              title: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row2_title_title",
                  "Choose title to be displayed"
                ),
              },
              scroll: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row2_scroll_title",
                  "Scroll"
                ),
                options: [
                  {
                    text: this.$t(
                      "eventpage.settings_display_scroll_lines_row2_scroll_options_donors",
                      "Donors"
                    ),
                    value: "donors",
                  },
                  {
                    text: this.$t(
                      "eventpage.settings_display_scroll_lines_row2_scroll_options_ambassadors",
                      "Ambassadors"
                    ),
                    value: "ambassadors",
                  },
                ],
              },
              show_amount_range_from: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row2_show_amount_range_from_title",
                  "Show amounts from"
                ),
              },
              show_amount_range_to: {
                title: this.$t(
                  "eventpage.settings_display_scroll_lines_row2_show_amount_range_to_title",
                  "to"
                ),
              },
            },
          },
          pin: {
            properties: {
              title: this.$t("eventpage.settings_display_pin_title", "Pin"),
              description: this.$t(
                "eventpage.settings_display_pin_description",
                "Option to pin donations or pin rows"
              ),
              icon: "lock",
              secondsection: {
                title: this.$t(
                  "eventpage.settings_display_pin_secondsection_title",
                  "Currently Pinned Donor Cards"
                ),
                icon: "lock",
              },
            },
            donation_total_or_ambassador_goal_above: {
              title: this.$t(
                "eventpage.settings_display_pin_donation_total_or_ambassador_goal_above_title",
                "Pin all published donations or ambassador goals that are above:"
              ),
            },
          },
          confetti: {
            properties: {
              title: this.$t("eventpage.settings_display_confetti_title", "Confetti"),
              description: this.$t(
                "eventpage.settings_display_confetti_description",
                "Control when confetti is automatically displayed"
              ),
              icon: "confetti",
            },
            show_confetti_on_donation_above: {
              title: this.$t(
                "eventpage.settings_display_confetti_show_confetti_on_donation_above_title",
                "Automatically display confetti when a donation higher than this amount is published:"
              ),
            },
            show_confetti_on_ambassador_goal_above: {
              title: this.$t(
                "eventpage.settings_display_confetti_show_confetti_on_ambassador_goal_above_title",
                "Automatically display confetti when an ambassador goal higher than this amount is published:"
              ),
            },
            show_confetti_on_total_raised_above_percent: {
              title: this.$t(
                "eventpage.settings_display_confetti_show_confetti_on_total_raised_above_percent_title",
                "Automatically display confetti when a this percentage of total goal is reached"
              ),
            },
            confetti_animation_type: {
              title: this.$t(
                "eventpage.settings_display_confetti_confetti_animation_type_title",
                "Animation type"
              ),
              options: [
                {
                  text: this.$t(
                    "eventpage.settings_display_confetti_confetti_animation_type_confetti",
                    "Confetti"
                  ),
                  value: "confetti",
                },
                {
                  text: this.$t(
                    "eventpage.settings_display_confetti_confetti_animation_type_leaf",
                    "Leaf"
                  ),
                  value: "leaf",
                },
              ],
            },
          },
          section_stats: {
            properties: {
              title: this.$t("eventpage.settings_display_section_stats_title", "Goals"),
              description: this.$t(
                "eventpage.settings_display_section_stats_description",
                "Choose which goals you want displayed"
              ),
              icon: "bd",
            },
            show_total_raised: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_total_raised_title",
                "Total Raised"
              ),
            },
            show_total_goal: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_total_goal_title",
                "Total Goal"
              ),
            },
            show_number_donors: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_number_donors_title",
                "Total Amount of Donors "
              ),
            },
            show_number_ambassadors: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_number_ambassadors_title",
                "Total Amount of Ambassadors"
              ),
            },
            show_total_ambassadors_goal: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_total_ambassadors_goal_title",
                "Total Ambassador Goal"
              ),
            },
            show_total_donation_pledges: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_total_donation_pledges_title",
                "Pledge Goal"
              ),
            },
            show_total_raised_in_percentage: {
              title: this.$t(
                "eventpage.settings_display_section_stats_show_total_raised_in_percentage_title",
                "Goal in Percentage"
              ),
            },
          },
        },
      };
    },

    initialSchema() {
      return {
        value: false,
        general: {
          language: this.orgLanguage,
          theme: "elegant",
          background_image: "",
          organization_logo: this.orgLogo,
          organization_logo_enabled: false,
          ...this.defaultColors,
        },
        display: {
          section_stats: {
            show_total_raised: false,
            show_total_goal: false,
            show_number_donors: false,
            show_number_ambassadors: false,
            show_total_ambassadors_goal: true,
            show_total_donation_pledges: false,
            show_total_raised_in_percentage: false,
          },
          confetti: {
            show_confetti_on_donation_above: 10000,
            donation_above_enabled: false,
            show_confetti_on_ambassador_goal_above: 10000,
            ambassador_goal_above_enabled: false,
            show_confetti_on_total_raised_above_percent: 50,
            total_raised_above_percent_enabled: false,
            confetti_animation_type: "confetti",
          },
          pin: {
            donation_total_or_ambassador_goal_above: null,
            donation_total_or_ambassador_goal_above_enabled: false,
            manual_list: [],
          },
          commitment_cards: {
            show_donors: false,
            show_ambassadors: true,
            show_joined_donors_and_ambassadors: false,
            mode: "manual",
            layout_cards_number: 26,
            same_time_commitment_timeout: 5,
          },
          scroll_lines: {
            row1: {
              active: false,
              scroll: "donors",
              title: "Silver donors",
              show_amount_range_from: 1000,
              show_amount_range_to: 5000,
            },
            row2: {
              active: true,
              scroll: "ambassadors",
              title: "Silver donors",
              show_amount_range_from: 1000,
              show_amount_range_to: 5000,
            },
          },
        },
      };
    },
  },

  watch: {
    campaignId: function () {
      this.showSettingsKey = "";
      this.search = "";
      this.campaignColors = null;
      this.initForm();
      this.getSetting();
    },

    filteredDonations: {
      immediate: true,
      handler(newList) {
        this.pinnedItems = newList.reduce((acc, item) => {
          const key = `${item.team_id}-${item.donation_id}`;
          acc[key] = true;
          return acc;
        }, {});
      },
    },

    eventSettingsList: {
      handler(newList) {
        this.form.display.pin.manual_list = newList?.display?.pin?.manual_list;
      },
    },

    "form.display.commitment_cards.mode": {
      handler(newMode) {
        if (newMode === "automatic") {
          // When automatic, cards should appear (so values should be true)
          this.new_don_cards_should_apear_value = true;
          this.new_team_cards_should_apear_value = true;
        } else if (newMode === "manual") {
          // When manual, cards should not appear automatically (so values should be false)
          this.new_don_cards_should_apear_value = false;
          this.new_team_cards_should_apear_value = false;
        }
      },
    },
  },

  methods: {
    openPreview() {
      const campaign = this.getSelectedCampaign;
      window.open(
        `https://ssr2.charidy.com/pre-event-platform/${campaign?.attributes?.short_link}`,
        "_blank"
      );
    },

    async addImg(event, id) {
      const file = event.target.files[0];
      if (!file) return;

      this.loadingImageId = id;

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(urls.loadImageQuill, formData);
        this.form.general[id] = response.data.src;
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        this.loadingImageId = null;
      }
    },
    showSetting(key) {
      this.search = "";
      this.showSettingsKey = key || "";
    },
    // Configurations saved before the rule switches existed get the switches
    // that match what the screen ran for them
    applyLegacyRuleSwitches(settings) {
      const pin = settings.display?.pin || {};
      const confetti = settings.display?.confetti || {};
      // The donor and ambassador rules ran only while both had an amount
      const donorAndAmbassadorOn =
        confetti.show_confetti_on_donation_above > 0 &&
        confetti.show_confetti_on_ambassador_goal_above > 0;
      const legacySwitches = {
        pin: {
          donation_total_or_ambassador_goal_above_enabled:
            typeof pin.donation_total_or_ambassador_goal_above === "number",
        },
        confetti: {
          donation_above_enabled: donorAndAmbassadorOn,
          ambassador_goal_above_enabled: donorAndAmbassadorOn,
          total_raised_above_percent_enabled:
            confetti.show_confetti_on_total_raised_above_percent > 0,
        },
      };

      Object.entries(legacySwitches).forEach(([section, switches]) => {
        Object.entries(switches).forEach(([key, on]) => {
          const saved = settings.display?.[section]?.[key];
          if (saved === undefined || saved === null) {
            this.form.display[section][key] = on;
          }
        });
      });

      // The screen showed the logo whenever one was set
      const logoSwitch = settings.general?.organization_logo_enabled;
      if (logoSwitch === undefined || logoSwitch === null) {
        this.form.general.organization_logo_enabled = Boolean(settings.general?.organization_logo);
      }
    },

    // Colors of the campaign itself, null when the campaign has not set its own
    async loadCampaignColors() {
      const campaignId = this.campaignId;
      try {
        const meta = await this.$store.dispatch("getMetaSettings", "campaign_colors");
        // A response for a campaign that is no longer selected is dropped
        if (campaignId !== this.campaignId) return;

        const colors = meta?.settingId ? meta.setting?.colors : null;
        this.campaignColors =
          colors?.primary && colors?.secondary
            ? { primary: colors.primary.trim(), secondary: colors.secondary.trim() }
            : null;
      } catch (e) {
        if (campaignId !== this.campaignId) return;

        this.$_notificationsMixin_handleCatch(e);
        this.campaignColors = null;
      }
    },

    // Switches the form between the screen colors and the campaign colors,
    // nothing changes until it is saved
    toggleColors() {
      Object.assign(this.form.general, this.usesScreenColors ? this.defaultColors : SCREEN_COLORS);
    },
    mergeServerDataIntoDefaults(defaults, serverData) {
      const result = defaults;

      for (const key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          if (Object.prototype.hasOwnProperty.call(serverData, key)) {
            const defaultValue = result[key];
            const serverValue = serverData[key];

            if (
              serverValue !== null &&
              typeof serverValue === "object" &&
              !Array.isArray(serverValue) &&
              defaultValue !== null &&
              typeof defaultValue === "object" &&
              !Array.isArray(defaultValue)
            ) {
              result[key] = this.mergeServerDataIntoDefaults(defaultValue, serverValue);
            } else if (serverValue !== undefined) {
              result[key] = structuredClone(serverValue);
            }
          }
        }
      }

      return result;
    },
    async getSetting() {
      this.loading = true;
      // The defaults read the campaign colors, so they have to arrive before the form is built
      const campaignColorsLoaded = this.loadCampaignColors();
      let settings = null;
      try {
        const data = await this.$store.dispatch("getEventSettings");
        await campaignColorsLoaded;
        if (data && data.attributes && data.attributes.meta_data) {
          settings = data.attributes.meta_data;
          this.form = this.mergeServerDataIntoDefaults(
            structuredClone(this.initialSchema),
            settings
          );
          this.applyLegacyRuleSwitches(data.attributes.meta_data);
        } else {
          this.form = structuredClone(this.initialSchema);
        }
      } catch (error) {
        console.error("Error fetching or merging settings:", error);
        this.$_notificationsMixin_handleCatch(error);
        await campaignColorsLoaded;
        this.form = structuredClone(this.initialSchema);
      }

      const promises = Object.keys(this.commonMeta).map((key) => {
        this.commonMeta[key].error = false;
        return this.$store
          .dispatch("getMetaSettings", this.commonMeta[key].metaName)
          .then((metaData) => {
            if (metaData) {
              this[`${key}_value`] = !!metaData?.setting?.[this.commonMeta[key].key];
              this.commonMeta[key].data = metaData;
            }
          })
          .catch((e) => {
            this.$_notificationsMixin_handleCatch(e);
            this.commonMeta[key].error = true;
            console.error(`Failed to get meta setting: ${this.commonMeta[key].metaName}`, e);
          });
      });

      try {
        await Promise.all(promises);
      } catch (e) {
        console.error("Error fetching one or more common meta settings:", e);
      }

      // Invert the values since the meta settings control hiding, not showing
      this.new_don_cards_should_apear_value = !this.new_don_cards_should_apear_value;
      this.new_team_cards_should_apear_value = !this.new_team_cards_should_apear_value;

      if (this.isNeverEnabled(settings)) {
        await this.applyFirstEnableDefaults(settings);
      }

      // Sync the mode with the current values
      if (this.new_don_cards_should_apear_value && this.new_team_cards_should_apear_value) {
        this.form.display.commitment_cards.mode = "automatic";
      } else if (
        !this.new_don_cards_should_apear_value &&
        !this.new_team_cards_should_apear_value
      ) {
        this.form.display.commitment_cards.mode = "manual";
      }
      // If mixed state, keep the current mode setting
      this.loading = false;
    },

    // Pins and confetti on the Donations tab save the settings without turning Event View on,
    // while turning it on saves both card metas, so a missing one means it was never on
    isNeverEnabled(settings) {
      const metas = Object.values(this.commonMeta);
      if (metas.some((meta) => meta.error)) return false;
      if (!settings) return true;
      return settings.value === false && !metas.every((meta) => meta.data?.settingId);
    },

    // Start from the defaults, keeping pinned cards and card metas the campaign already has
    async applyFirstEnableDefaults(settings) {
      const form = structuredClone(this.initialSchema);
      form.display.pin.manual_list = structuredClone(settings?.display?.pin?.manual_list || []);
      this.form = form;

      // The mode watcher resets both card switches when the mode changes, let it run first
      await this.$nextTick();
      Object.keys(this.commonMeta).forEach((key) => {
        if (!this.commonMeta[key].data?.settingId) {
          this[`${key}_value`] = this.commonMeta[key].firstEnableValue;
        }
      });
    },

    async save() {
      this.form.display.pin.manual_list = this.form.display.pin.manual_list.filter(
        (pinned) => this.pinnedItems[`${pinned.team_id}-${pinned.donation_id}`] !== false
      );

      if (!this.form.general.language || typeof this.form.general.language !== "string") {
        this.form.general.language = "en";
      }

      const promises = Object.keys(this.commonMeta).map(async (key) => {
        const valueKey = `${key}_value`;
        // Invert the value since the meta settings control hiding, not showing
        const value = !this[valueKey];

        if (this.commonMeta[key].error) {
          throw new Error(this.commonMeta[key].error);
        }

        await this.$store.dispatch("saveMetaSettings", {
          ...this.commonMeta[key],
          value,
          key: this.commonMeta[key].key,
        });

        return this.commonMeta[key];
      });

      try {
        const data = await this.$store.dispatch("saveEventSettings", { form: this.form });
        this.form = structuredClone(data.attributes.meta_data);

        const results = await Promise.allSettled(promises);
        const metaNames = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value.metaName);

        this.$_notificationsMixin_makeToast(
          "Success",
          this.$t(
            `eventpage.settings_meta_saved`,
            "Event settings saved.\n{metaNames} saved successfully",
            {
              metaNames: metaNames.join(", "),
            }
          ),
          "success"
        );
      } catch (e) {
        this.$_notificationsMixin_handleCatch(e);
        throw e;
      }
    },

    initForm() {
      this.form = structuredClone(this.initialSchema);
      this.$store.dispatch("setInitialEventSettings", this.form);
    },

    onFormValueInput(value) {
      const prevForm = structuredClone(this.form);
      this.form.value = value;

      this.save().catch(() => {
        this.form.value = prevForm.value;
      });
    },
  },

  created() {
    this.initForm();
  },

  mounted() {
    this.getSetting();
  },
};
</script>

<style lang="scss" scoped>
.event-controls-hero {
  padding-block: 100px;
  @media (max-width: 768px) {
    padding-block: 60px;
  }
}

.event-search {
  padding-block-start: 82px;
}

.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 46px;
  color: var(--event-black);
  font-size: 20px;

  .form-section {
    &__header {
      padding: 17px 86px;
      border: 1px solid var(--event-dark-blue);
      border-top-left-radius: 20px;
      border-top-right-radius: 20px;
      font-weight: 700;
      background-color: var(--event-dark-blue);
      color: var(--event-white);
    }
  }
}

.event-checkbox-helper {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  padding-block: 36px;

  &__label {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__text {
    width: 166px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.event-radio-group-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding-inline-start: var(--padding-left);
  padding-block: 36px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding-inline-start: 0;
  }
}

::v-deep .event-radio-group {
  flex-shrink: 0;
  margin-bottom: 0;
  @media (max-width: 768px) {
    flex-shrink: 1;
  }

  .btn-group {
    gap: 8px;
    @media (max-width: 768px) {
      flex-wrap: wrap;
    }
  }

  .btn-primary {
    height: auto;
    padding: 14px;
    border-radius: 15px !important;
    border-color: var(--event-not-active);
    background-color: var(--event-not-active);
    font-family: inherit !important;
    font-weight: 400 !important;
    font-size: 19px;
    line-height: 1;
    @media (max-width: 768px) {
      flex-grow: 0;
    }
  }

  .btn-primary:not(:disabled):not(.disabled).active {
    border-color: var(--event-primary);
    background-color: var(--event-primary);
    color: var(--event-white);
  }

  .btn-primary:not(:disabled):not(.disabled):active {
    border-color: var(--event-primary);
    background-color: var(--event-primary);
  }
}

.event-scrolling-settings-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding-block: 36px;

  &__input {
    padding-block: 0;
  }
}

.event-rule-switch {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;

  .input {
    flex: 1 1 auto;
    min-width: 0;
  }

  .event-checkbox {
    flex: none;
  }

  &--off ::v-deep .input__container {
    opacity: 0.5;
  }
}

.event-two-inputs-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding-inline-start: var(--padding-left);
  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding-inline-start: 0;
  }

  &__title {
    margin-right: auto;
    @media (max-width: 768px) {
      margin-right: 0;
    }
  }

  &__input {
    width: auto;
    padding-block: 0;
    padding-left: 0;
  }
}

::v-deep .event-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding-inline-start: var(--padding-left);
  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding-inline-start: 0;
  }

  &__select {
    width: 294px;

    .multiselect {
      color: var(--event-soft-dark);
      font-size: 23px;
      cursor: pointer;
    }

    .multiselect__select {
      position: absolute;
      top: 0px;
      right: 0px;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 80px;
      transition: transform 0.2s ease;

      &::before {
        content: none;
      }

      svg {
        transform: rotate(90deg);
      }
    }

    .multiselect__single {
      padding-left: 12px;
      font-size: 23px;
      background-color: var(--event-ui-bg);
    }

    .multiselect__tags {
      min-height: 62px;
      padding-top: 16px;
      padding-right: 80px;
      border-color: transparent;
      border-radius: 9999px;
      background-color: var(--event-ui-bg);
    }

    .multiselect__content-wrapper {
      border-color: transparent;
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }

    .multiselect--active {
      .multiselect__select {
        transform: rotate(180deg);
      }
      .multiselect__content-wrapper {
        box-shadow: 0px 2.26878px 22.6878px rgba(0, 0, 0, 0.05);
      }
      .multiselect__tags {
        border-radius: 0;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      }
    }

    .multiselect__option {
      padding: 20px 30px;
      line-height: 23px;
    }

    .multiselect__option--highlight {
      background-color: transparent;
      color: var(--event-primary);
    }

    .multiselect__option--selected {
      background-color: transparent;
      color: var(--event-primary);
      font-weight: 400;
    }
  }
}

.event-color-theme-settings-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 27px 22px 37px 31px;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;
    padding-inline-start: 24px;

    &:first-child {
      border-right: 1px solid var(--event-border);
      padding-inline-end: 24px;
      padding-inline-start: 0;

      [dir="rtl"] & {
        border-left: 1px solid var(--event-border);
        border-right: none;
      }
    }

    img {
      margin-top: 30px;
      margin-bottom: 42px;
      border: 2px solid transparent;
      border-radius: 10px;
      &.active {
        border: 2px solid var(--event-primary);
      }
    }
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
    &__item {
      border: 0 !important;
      padding: 0 !important;
      img {
        margin-top: 20px;
        margin-bottom: 26px;
      }
    }
  }
}

.event-color-settings-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
  padding-block: 36px;
  padding-inline-start: var(--padding-left);
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding-inline-start: 0;
  }

  &__title {
    margin-bottom: 10px;
    grid-column: 1 / -1;

    [dir="rtl"] & {
      text-align: right;
    }
  }

  &-controls {
    display: flex;
    flex-direction: column;
    gap: 36px;

    &__item {
      display: flex;
      flex-direction: column;
      & > p {
        margin-bottom: 16px;
      }

      & > div {
        display: flex;
        align-items: center;
        gap: 20px;
      }
    }
  }

  &-result {
    padding-top: 24px;
    margin: 0 auto;
  }

  & &__logo-switch {
    grid-column: 1 / -1;
    padding-inline-start: 0;
  }

  &-controls__hint {
    margin-top: 8px;
    color: var(--event-secondary-c);
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    grid-column: 1 / -1;
  }

  &-files {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    justify-content: space-between;
    grid-column: 1 / -1;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    img {
      grid-column: 1 / -1;
    }

    &-text {
      max-width: 360px;
      @media (max-width: 768px) {
        max-width: 100%;
      }

      p {
        margin-top: 20px;
        margin-bottom: 0;
      }
      span {
        font-size: 17px;
        font-style: italic;
      }
    }
  }
}

.event-commitment-cards-controlled-setting {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  .text-muted {
    font-size: 14px;
    color: #6c757d;
    font-style: italic;
    padding-inline-end: 52px;
    padding-inline-start: var(--padding-left);
  }
}

.event-input-color-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 100%;
  aspect-ratio: 1 / 1;
  padding: 10px;
  border: 1px solid var(--event-border);
  border-radius: 9999px;

  &-inner {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 9999px;
  }

  .event-input-color {
    position: absolute;
    top: -50%;
    left: -50%;
    width: calc(100% * 2);
    height: calc(100% * 2);
    padding: 0;
    border: 0;
    background-color: transparent;
    line-height: 1;
    outline: none;
  }
}

.event-input-color-text {
  width: 100%;
  padding: 20px 36px;
  border: 1px solid var(--event-border);
  border-radius: 9999px;
  background-color: transparent;
  line-height: 1;
  outline: none;
}
</style>
